import { Theme, css, useTheme } from '@emotion/react';

import React, { PropsWithChildren, ReactNode, useEffect, useRef } from 'react';

import { Radius, Spacing } from 'chatie-design-system';
import { Close, Icon } from 'chatie-design-system/icon';

import { MODAL_HEADER_HEIGHT, safeArea } from '@styles/css';
import { CSSProps, OnClickProps } from '@utils/misc';

import Push, { PushSize } from '../layout/Push';
import Button, { ButtonProps } from './Button';
import Overlay, { OverlayProps } from './Overlay';

export const bottomModalCSS = (isOpen: boolean, contentSize?: string) =>
  css`
    width: 100%;
    margin: 0 auto;
    position: absolute;
    bottom: 0;
    border-top-left-radius: ${Radius.large}px;
    border-top-right-radius: ${Radius.large}px;
    overflow: hidden;

    ${contentSize === 'fit'
      ? css`
          max-height: 80%;
        `
      : css`
          height: ${contentSize};
        `}

    transition: 0.4s cubic-bezier(0.25, 1, 0.5, 1);
    will-change: transform;

    ${isOpen
      ? css`
          transform: translateY(0);
          visibility: visible;
        `
      : css`
          transform: translateY(100%);
          visibility: hidden;
        `}
  `;

export const headerWrapperCSS = css`
  width: 100%;
`;

export const headerCSS = (theme: Theme) =>
  css`
    justify-content: space-between;
    padding: 0 ${Spacing.medium}px;
    height: ${MODAL_HEADER_HEIGHT}px;
    border-bottom: 1px solid ${theme.border.light};
    background-color: ${theme.var.white};

    h4 {
      text-align: center;
      line-height: ${MODAL_HEADER_HEIGHT}px;
    }
  `;

const headerLeftButtonCSS = css`
  position: absolute;
  top: 10px;
  left: ${Spacing.small}px;
`;

export const headerCloseButtonCSS = css`
  position: absolute;
  top: 10px;
  right: ${Spacing.small}px;
`;

export const contentCSS = (theme: Theme) =>
  css`
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    max-height: inherit;
    padding-bottom: ${MODAL_HEADER_HEIGHT}px;
    overflow: auto;
    background-color: ${theme.var.white};
  `;

export interface BottomModalProps extends OverlayProps {
  title?: string;
  contentSize?: 'fit' | '80%' | '70%' | '60%';
  leftButton?: (color: string) => ReactNode;
  renderButton?: () => ReactNode;
  close?: boolean;
  header?: boolean;
  hideHeaderBorder?: boolean;
}

const BottomModal = ({
  title,
  contentSize,
  children,
  leftButton,
  renderButton,
  className,
  close = true,
  header = true,
  ...rest
}: PropsWithChildren<BottomModalProps> & CSSProps) => {
  const { open, onClose } = rest;

  const theme = useTheme();

  const contentContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      if (contentContainerRef.current) {
        contentContainerRef.current.scrollTop = 0;
      }
    }
  }, [open]);

  return (
    <Overlay {...rest} className={className}>
      <div css={bottomModalCSS(open, contentSize)}>
        {header && (
          <header css={headerWrapperCSS}>
            <div css={headerCSS}>
              {leftButton && (
                <div css={headerLeftButtonCSS}>
                  {leftButton(theme.var.black)}
                </div>
              )}
              <h4>{title}</h4>
              {close && (
                <div css={headerCloseButtonCSS}>
                  <button
                    className="icon-btn"
                    type="button"
                    onClick={() => onClose()}
                  >
                    <Icon icon={Close} color={theme.var.black} />
                  </button>
                </div>
              )}
            </div>
          </header>
        )}

        <div css={contentCSS} ref={contentContainerRef}>
          {children}
          {renderButton && <Push bottom size={PushSize.bottomButton} />}
        </div>
        {renderButton?.()}
      </div>
    </Overlay>
  );
};

/** Button */
const bottomModalBtnCSS = css`
  position: absolute;
  width: 100%;
  left: 0;
  bottom: 0;
  padding-left: ${Spacing.medium}px;
  padding-right: ${Spacing.medium}px;
  z-index: 9;
  ${safeArea('bottom', 'padding', Spacing.medium)}

  @media (max-height: 340px) {
    display: none;
  }
`;

export const BottomModalButton = ({
  className,
  text,
  disabled = false,
  onClick,
}: ButtonProps & OnClickProps & CSSProps) => {
  return (
    <div css={bottomModalBtnCSS} className={className}>
      <Button text={text} disabled={disabled} onClick={onClick} />
    </div>
  );
};

export default BottomModal;
